import { TrendingDown, TrendingUp } from 'lucide-react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'

import { hasEquivalentPrior, priorPeriodLabel } from './filters/dateRange'
import type { DateFilter } from './filters/types'

import type { TeamMembersMode, TeamMembersResponse } from '@/api/teamInsights'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn, formatAxisNumber } from '@/lib/utils'

const LINE_COLOR = 'var(--chart-1)'

const MODES: {
  value: TeamMembersMode
  label: string
  pctChangeLabel: string
}[] = [
  { value: 'daily', label: 'Daily active users', pctChangeLabel: 'from same day last week' },
  { value: 'weekly', label: 'Weekly active users', pctChangeLabel: 'from prior 7 days' },
  { value: 'monthly', label: 'Monthly active users', pctChangeLabel: 'from prior 30 days' },
]

const chartConfig = {
  count: { label: 'Team members', color: LINE_COLOR },
} satisfies ChartConfig

const formatDayWithYear = (d: Date) =>
  d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })

// `period` is the END of a [D−N, D] rolling window; tooltip clamps it to the
// active filter so the partial leftmost bar reads as its actual slice. Filter
// Dates are UTC-midnight (see filters/dateRange.ts) — compare raw.
const formatTooltipDate = (period: string, mode: TeamMembersMode, filterStart?: Date, filterEnd?: Date): string => {
  const end = new Date(`${period}T00:00:00Z`)
  if (mode === 'daily') {
    return formatDayWithYear(end)
  }
  if (mode === 'weekly' || mode === 'inactive') {
    let start = new Date(end)
    start.setUTCDate(start.getUTCDate() - 6)
    let clampedEnd = end
    if (filterStart && filterStart > start) start = filterStart
    if (filterEnd && filterEnd < clampedEnd) clampedEnd = filterEnd
    if (start.getTime() === clampedEnd.getTime()) {
      return formatDayWithYear(start)
    }
    return `${formatDayWithYear(start)} – ${formatDayWithYear(clampedEnd)}`
  }
  return end.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

const formatAxisDate = (period: string, mode: TeamMembersMode, filterStart?: Date): string => {
  const date = new Date(`${period}T00:00:00Z`)
  if (mode === 'monthly') {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      timeZone: 'UTC',
    })
  }
  // `period` is the window END; shift back 6 days for the tick. Clamp to
  // filterStart so a partial leftmost bar reads as its actual slice.
  if (mode === 'weekly' || mode === 'inactive') {
    date.setUTCDate(date.getUTCDate() - 6)
    if (filterStart && filterStart > date) {
      return filterStart.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    }
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

interface TeamMembersChartProps {
  response: TeamMembersResponse
  mode: TeamMembersMode
  onModeChange: (mode: TeamMembersMode) => void
  dateFilter: DateFilter
  filterStart?: Date
  filterEnd?: Date
  hideModeSelect?: boolean
  contentOverlay?: React.ReactNode
}

const TeamMembersChart = ({
  response,
  mode,
  onModeChange,
  dateFilter,
  filterStart,
  filterEnd,
  hideModeSelect = false,
  contentOverlay,
}: TeamMembersChartProps) => {
  const modeConfig = MODES.find((m) => m.value === mode) ?? MODES[1]
  // Mode-matched window when today-inclusive, equivalent prior duration when
  // the range excludes today (see schema for full semantics).
  const pctChange = response.pct_change_vs_prev_period ?? null
  const isPositive = pctChange !== null && pctChange > 0
  const isNegative = pctChange !== null && pctChange < 0
  const TrendIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : null
  // With a start_date: equivalent prior duration (preset-aware label).
  // Pure lifetime: mode-matched window (DAU vs same-day-last-week, etc.).
  const pctChangeLabel = hasEquivalentPrior(dateFilter) ? priorPeriodLabel(dateFilter) : modeConfig.pctChangeLabel

  return (
    <Card className="pt-0">
      <CardHeader className="flex items-center gap-4 space-y-0 border-b pl-6 pr-3 !py-3 sm:flex-row">
        <div className="flex flex-1 items-center gap-4">
          <CardTitle className="text-xl font-semibold">{modeConfig.label}</CardTitle>
          {pctChange !== null && (
            <div
              className={cn(
                'flex items-center gap-1 text-[15px] font-semibold leading-5',
                isPositive ? 'text-[var(--green-600)]' : 'text-muted-foreground'
              )}
            >
              {TrendIcon && <TrendIcon className="size-5" />}
              <span>
                {Math.abs(pctChange)}% {pctChangeLabel}
              </span>
            </div>
          )}
        </div>
        {!hideModeSelect && (
          <CardAction>
            <Select value={mode} onValueChange={(v) => onModeChange(v as TeamMembersMode)}>
              <SelectTrigger className="w-auto rounded-lg text-[15px] leading-5" aria-label="Select active-user mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {MODES.map((m) => (
                  <SelectItem key={m.value} value={m.value} className="rounded-lg">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="relative pl-0 pr-4 py-0">
        {contentOverlay && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-6">
            {contentOverlay}
          </div>
        )}
        <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
          <LineChart accessibilityLayer data={response.timeseries} margin={{ top: 12, right: 12, left: 12, bottom: 0 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="period"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              interval="preserveStartEnd"
              tickFormatter={(value) => formatAxisDate(value as string, mode, filterStart)}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={48}
              allowDecimals={false}
              domain={[0, 'auto']}
              tickFormatter={(value) => (value === 0 ? '' : formatAxisNumber(value as number))}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => formatTooltipDate(value as string, mode, filterStart, filterEnd)}
                  indicator="dot"
                />
              }
            />
            <Line dataKey="count" type="monotone" stroke="var(--color-count)" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

export default TeamMembersChart
